/**
 * Demo-seed master switch (task #992 — go-live with REAL master data).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY
 * ─────────────────────────────────────────────────────────────────────────
 *  Task #888 intentionally MERGED the code-level demo seed (the VP656
 *  reviewer fixture + the 4 DEMO_PROJECTS scaffold rows + the ancillary
 *  picker's demo project/client source) with live Firestore data so the
 *  Step-6 screens were fully walkable before any real data existed.
 *
 *  The owner is now loading the real client+project master (the importer).
 *  The portal must show ONLY live Firestore data — no fake "Sitaramji
 *  Agarwal / VP656 / Meera Krishnan" rows injected by code.
 *
 *  This is the SINGLE switch. Default = OFF (no demo seed) so a normal
 *  `npm run dev` and every production build are clean. A reviewer who
 *  still wants the old walkthrough sets `VITE_DEMO_SEED=1` explicitly.
 *
 *  The PURE seed constants (DEMO_PROJECTS, SEED_LEADS, …) are NOT removed
 *  — the Node test suite imports them directly as deterministic fixtures.
 *  Only the SCREEN-FACING accessors consult this flag and return empty
 *  when the seed is off, so the live-data code paths are untouched and the
 *  contract tests stay green.
 *
 *  Defensive read mirrors firebase.ts's FN_EMULATOR pattern: under the
 *  Node test runner `import.meta.env` is undefined, so a bare access
 *  throws and takes down every transitive importer. Optional-chain so
 *  test-time loading is safe; Vite still inlines the real env at build.
 * ─────────────────────────────────────────────────────────────────────────
 */

let _override: boolean | null = null;

/**
 * TRUE only when the demo seed is explicitly enabled via
 * `VITE_DEMO_SEED=1`. Default (prod + normal dev) = FALSE → the portal
 * renders ONLY live Firestore data. Tests may force a value via
 * `__setDemoSeedForTest`.
 */
export function demoSeedEnabled(): boolean {
  if (_override !== null) return _override;
  const env =
    typeof import.meta !== 'undefined'
      ? (import.meta as { env?: Record<string, string | undefined> }).env
      : undefined;
  return env?.VITE_DEMO_SEED === '1';
}

/** Test-only knob (the contract tests assert both states). */
export function __setDemoSeedForTest(v: boolean | null): void {
  _override = v;
}
